import React, { useState, useEffect } from 'react';

import Button from '../../../../../../../../components/global/Button';
import useSaveTheDateSetup from './hooks/useSaveTheDateSetup';
import useOpenModal from '../../../../../../../../hooks/useOpenModal';
import {
  ACCOUNT_CARD_CONFIGURATOR_MODAL,
  AccountCardConfiguratorModal,
} from '../../../../../../../../services/cards/views/AccountCardConfigurator';
import useCloseModal from '../../../../../../../../hooks/useCloseModal';
import useRequestConfirm from '../../../../../../../../hooks/useRequestConfirm';
import { getErrorString } from '../../../../../../../../util/normalize-errors';
import useOpenAlert from '../../../../../../../../hooks/useOpenAlert';
import { ACCOUNT_CARD_STATUS } from '../../../../../../../../services/cards/constants';
import { isAlbumFolderOwner } from '../../../../../../../../util/account';
import { isFutureEvent } from './helpers';

const SaveTheDateSetup = ({ albumFolder }) => {
  const openModal = useOpenModal();
  const closeModal = useCloseModal();
  const openAlert = useOpenAlert();
  const requestConfirm = useRequestConfirm();
  const [currentAccountCard, setCurrentAccountCard] = useState(null);
  const [reviewingMode, setReviewingMode] = useState(false);
  const [saving, setSaving] = useState(false);
  const {
    account,
    loadingSaveTheDates,
    saveTheDateCategory,
    saveTheDateAccountCard,
    onAccountCardDelete,
    onCheckoutSuccess,
  } = useSaveTheDateSetup({
    albumFolderId: albumFolder?.id,
  });

  const isOwner = isAlbumFolderOwner(account, albumFolder);
  const isFutureEventBool = isFutureEvent(albumFolder?.event_settings);

  useEffect(() => {
    if (saveTheDateAccountCard) {
      setCurrentAccountCard(saveTheDateAccountCard);
    }
  }, [saveTheDateAccountCard]);

  if (
    !isOwner ||
    !isFutureEventBool ||
    !saveTheDateCategory ||
    loadingSaveTheDates
  ) {
    return null;
  }

  const onModalClose = () => {
    if (!saveTheDateAccountCard && currentAccountCard) {
      requestConfirm({
        actionSize: 'xs',
        message: (
          <p className="text-center">
            <b>Are you sure you want to exit?</b>
            <br /> <br />
            Exiting now will delete the current card.
          </p>
        ),
        confirmText: 'Exit',
        cancelText: 'Cancel',
        onConfirm: async () => {
          if (saving) {
            return;
          }

          setSaving(true);
          try {
            await onAccountCardDelete(currentAccountCard);
            closeModal(ACCOUNT_CARD_CONFIGURATOR_MODAL);
            setCurrentAccountCard(null);
          } catch (e) {
            openAlert({ type: 'error', message: getErrorString(e) });
          } finally {
            setSaving(false);
          }
        },
        onCancel: () => {
          // Do nothing
        },
      });
    } else {
      closeModal(ACCOUNT_CARD_CONFIGURATOR_MODAL);
    }
  };

  return (
    <div className="mt-4">
      <h4 className="mb-0">Printed Save-the-Date cards</h4>
      <p className="text-bold text-light text-sm">
        Personalize your own Save-the-Date cards using our beautiful templates
        &ndash; Waldo will print and mail it for you!
      </p>
      {!saveTheDateAccountCard && (
        <div className="d-flex">
          <Button
            disabled={saving}
            onClick={() =>
              openModal(ACCOUNT_CARD_CONFIGURATOR_MODAL, {
                actionHideHeader: true,
                actionDismissible: false,
              })
            }
          >
            Create Save-the-date card
          </Button>
        </div>
      )}
      {!!saveTheDateAccountCard && (
        <div className="d-flex">
          {saveTheDateAccountCard.status === ACCOUNT_CARD_STATUS.SHIPPED && (
            <Button
              className="mr-1"
              disabled={saving}
              onClick={() => {
                setReviewingMode(true);
                openModal(ACCOUNT_CARD_CONFIGURATOR_MODAL, {
                  actionHideHeader: true,
                  actionDismissible: false,
                });
              }}
            >
              Send more
            </Button>
          )}
          <Button
            btnOutline
            disabled={saving}
            onClick={() =>
              openModal(ACCOUNT_CARD_CONFIGURATOR_MODAL, {
                actionHideHeader: true,
                actionDismissible: false,
              })
            }
          >
            Edit design
          </Button>
        </div>
      )}
      <AccountCardConfiguratorModal
        hideHeader
        onClose={onModalClose}
        title="Send a Save The Date Card to your guests!"
        accountCardAlbumFolderId={albumFolder?.id}
        defaultCardCategoryId={saveTheDateCategory?.id}
        onAccountCardChanged={({ accountCard, setReviewing }) => {
          setCurrentAccountCard(accountCard);
          if (reviewingMode) {
            setReviewing(true);
            setReviewingMode(false);
          }
        }}
        onCardSelectClose={() => closeModal(ACCOUNT_CARD_CONFIGURATOR_MODAL)}
        onCheckoutSuccess={async () => {
          await onCheckoutSuccess();
          closeModal(ACCOUNT_CARD_CONFIGURATOR_MODAL);
        }}
        accountCardId={saveTheDateAccountCard?.id}
      />
    </div>
  );
};

export default SaveTheDateSetup;
